'use client'

import { useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import { AppQRCode } from './AppQRCode'
import { APP_CONFIG } from '../lib/config'
import { buildUtmNoteFromQuery } from '../lib/utm'
import { activationTranslations, ActivationLocale } from '../translations/activation'

const CODE_LENGTH = 8
const GLYPHS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'

interface GetInviteFlowProps {
  locale?: string
  accentColor?: string
  title?: string | null
  description?: string
}

type FlowState = 'email' | 'submitting' | 'success'

export function GetInviteFlow({
  locale = 'en',
  accentColor = '#C5ECAC',
  title,
  description,
}: GetInviteFlowProps) {
  const t = activationTranslations[locale as ActivationLocale] || activationTranslations.en
  const [email, setEmail] = useState('')
  const [state, setState] = useState<FlowState>('email')
  const [error, setError] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [displayCode, setDisplayCode] = useState('•'.repeat(CODE_LENGTH))
  const [lockedCount, setLockedCount] = useState(0)
  const [copied, setCopied] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const submitTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const scrambleTimer = useRef<ReturnType<typeof setInterval> | null>(null)
  const lockTimers = useRef<Array<ReturnType<typeof setTimeout>>>([])
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const badgeLang = locale === 'de' ? 'de' : 'en'
  const appStoreBadge = `/images/invite/icon_download_apple-app-store_dark_${badgeLang}.svg`
  const playStoreBadge = `/images/invite/icon_download_google-play_dark_${badgeLang}.svg`

  const clearTimers = () => {
    if (submitTimer.current) clearTimeout(submitTimer.current)
    if (scrambleTimer.current) clearInterval(scrambleTimer.current)
    if (copiedTimer.current) clearTimeout(copiedTimer.current)
    lockTimers.current.forEach(clearTimeout)
    submitTimer.current = null
    scrambleTimer.current = null
    copiedTimer.current = null
    lockTimers.current = []
  }

  useEffect(() => {
    const media = window.matchMedia('(max-width: 720px)')
    const update = () => setIsMobile(media.matches)
    update()
    media.addEventListener('change', update)
    return () => media.removeEventListener('change', update)
  }, [])

  useEffect(() => {
    return clearTimers
  }, [])

  useEffect(() => {
    if (state !== 'success' || !code) return

    clearTimers()
    setCopied(false)
    setLockedCount(0)
    setDisplayCode(randomCode(code.length))

    scrambleTimer.current = setInterval(() => {
      setDisplayCode(() =>
        code
          .split('')
          .map((char, index) => index < lockedCountRef.current ? char : randomGlyph())
          .join('')
      )
    }, 45)

    code.split('').forEach((_, index) => {
      const timer = setTimeout(() => {
        setLockedCount(index + 1)
        setDisplayCode(current =>
          code
            .split('')
            .map((char, charIndex) => charIndex <= index ? char : current[charIndex] || randomGlyph())
            .join('')
        )
        if (index === code.length - 1 && scrambleTimer.current) {
          clearInterval(scrambleTimer.current)
          scrambleTimer.current = null
          setDisplayCode(code)
        }
      }, 430 + index * 115)
      lockTimers.current.push(timer)
    })
  }, [state, code])

  const lockedCountRef = useRef(lockedCount)
  useEffect(() => {
    lockedCountRef.current = lockedCount
  }, [lockedCount])

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError(t.invalidEmail)
      return
    }

    setError(null)
    setState('submitting')
    try {
      const note = buildUtmNoteFromQuery(
        typeof window !== 'undefined' ? window.location.search : null
      )
      const res = await fetch('/api/invite/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'website-homepage', note }),
      })
      if (!res.ok) throw new Error(`request failed: ${res.status}`)
      const data = await res.json()
      if (typeof data?.code !== 'string' || !data.code) throw new Error('no code in response')
      setCode(data.code)
      setState('success')
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.error('invite request failed:', err)
      setError(t.requestError)
      setState('email')
    }
  }

  const handleCopy = () => {
    if (!code) return
    const write = navigator.clipboard?.writeText
      ? navigator.clipboard.writeText(code)
      : Promise.resolve()
    write
      .then(() => setCopied(true))
      .catch(() => setCopied(true))
    if (copiedTimer.current) clearTimeout(copiedTimer.current)
    copiedTimer.current = setTimeout(() => setCopied(false), 1900)
  }

  if (state === 'success') {
    const revealDone = lockedCount === code.length

    return (
      <div className="flex flex-col">
        <div>
          <h3 id="waitlist-popover-title" className="font-display text-[22px] font-medium leading-none tracking-[-0.03em] text-[#0c1a14]">{t.codeReady}</h3>
          <p className="mt-1 text-[14px] font-semibold tracking-[-0.02em] text-[#5a8a3f]">{t.sentTo} {email}</p>
        </div>

        <button
          type="button"
          onClick={handleCopy}
          className={`invite-code-chip relative mt-6 flex min-h-[76px] w-full items-center justify-center overflow-hidden rounded-2xl px-12 text-center ${revealDone ? 'invite-code-chip-ready' : ''}`}
          style={{
            background: '#ffffff',
            border: '1px solid rgba(12,26,20,0.10)',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.72)',
            backdropFilter: 'blur(24px) saturate(1.4)',
            WebkitBackdropFilter: 'blur(24px) saturate(1.4)',
          }}
          aria-label={t.copyCodeLabel}
        >
          <span className="font-display text-[30px] font-normal tracking-[0.16em] text-[#0c1a14] sm:text-[36px] sm:tracking-[6px]">
            {displayCode.split('').map((char, index) => (
              <span key={`${char}-${index}`} style={{ color: index < lockedCount ? '#0c1a14' : 'rgba(12,26,20,0.32)' }}>
                {char}
              </span>
            ))}
          </span>
          <span className="absolute right-5 top-1/2 z-10 flex -translate-y-1/2 items-center justify-end gap-1.5 text-sm font-medium text-[#091410]/45">
            {copied ? (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M5 12.5l4.2 4.2L19 7" stroke="#091410" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span style={{ color: '#091410' }}>{t.copied}</span>
              </>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <rect x="8" y="8" width="10" height="10" rx="2" stroke="currentColor" strokeWidth="1.8" />
                <path d="M6 14H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7a2 2 0 0 1 2 2v1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            )}
          </span>
        </button>

        <div className="my-[22px] h-px bg-[rgba(12,26,20,0.08)]" />

        <h4 className="font-display text-[22px] font-medium leading-none tracking-[-0.03em] text-[#0c1a14]">{isMobile ? t.downloadTitle : t.scanTitle}</h4>

        {isMobile ? (
          <div className="mt-5 flex flex-col gap-3">
            <a href={APP_CONFIG.downloads.ios.appStoreUrl} target="_blank" rel="noopener noreferrer">
              <Image src={appStoreBadge} alt={t.appStoreAlt} width={168} height={50} className="h-auto w-[168px]" />
            </a>
            <a href={APP_CONFIG.downloads.android.playStoreUrl} target="_blank" rel="noopener noreferrer">
              <Image src={playStoreBadge} alt={t.playStoreAlt} width={168} height={50} className="h-auto w-[168px]" />
            </a>
          </div>
        ) : (
          <div className="mt-6 flex justify-center">
            <div className="flex h-[220px] w-[220px] shrink-0 items-center justify-center rounded-[20px] bg-white p-4 shadow-[0_18px_40px_rgba(9,20,16,0.16)]">
              <AppQRCode size={188} className="h-auto w-full" />
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      {title !== null && (
        <h3 id="waitlist-popover-title" className="max-w-[360px] pr-14 font-display text-[30px] font-normal leading-[1.03] tracking-[-1px] text-[#0c1a14] sm:pr-0">
          {title ?? t.modalTitle}
        </h3>
      )}
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <p className="text-[16px] leading-relaxed text-[#3a463f]">
          {description ?? t.modalDescription}
        </p>
        <input
          type="email"
          placeholder={t.emailPlaceholder}
          value={email}
          onChange={(event) => { setEmail(event.target.value); setError(null) }}
          disabled={state === 'submitting'}
          className="h-[58px] w-full rounded-full px-5 text-[16px] text-[#0c1a14] placeholder:text-[#3a463f]/55 outline-none transition-all"
          style={{
            background: 'rgba(255,255,255,0.5)',
            border: `1px solid ${error ? 'rgba(255,107,107,0.8)' : 'rgba(255,255,255,0.62)'}`,
            boxShadow: error ? '0 0 0 4px rgba(255,107,107,0.18)' : undefined,
            backdropFilter: 'blur(18px)',
            WebkitBackdropFilter: 'blur(18px)',
          }}
          onFocus={event => {
            event.currentTarget.style.borderColor = 'rgba(146,213,109,0.75)'
            event.currentTarget.style.boxShadow = '0 0 0 4px rgba(146,213,109,0.28)'
          }}
          onBlur={event => {
            event.currentTarget.style.borderColor = error ? 'rgba(255,107,107,0.8)' : 'rgba(255,255,255,0.62)'
            event.currentTarget.style.boxShadow = error ? '0 0 0 4px rgba(255,107,107,0.18)' : ''
          }}
        />
        {error && <p className="text-sm text-coral">{error}</p>}
        <button
          type="submit"
          disabled={state === 'submitting'}
          className="flex h-[62px] w-full items-center justify-between rounded-full px-7 pr-2.5 font-display text-[16px] font-medium transition-transform duration-200 hover:-translate-y-0.5 disabled:cursor-wait"
          style={{
            background: '#091410',
            color: '#faf9f5',
            boxShadow: '0 16px 34px rgba(9,20,16,0.24)',
          }}
        >
          <span>{state === 'submitting' ? t.creatingCode : t.createCode}</span>
          <span
            className="flex h-[42px] w-[42px] items-center justify-center rounded-full text-[#091410]"
            style={{ background: accentColor }}
          >
            {state === 'submitting' ? (
              <span className="invite-spinner h-[19px] w-[19px] rounded-full" />
            ) : (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14" />
                <path d="m12 5 7 7-7 7" />
              </svg>
            )}
          </span>
        </button>
      </form>
    </div>
  )
}

function randomGlyph() {
  return GLYPHS[Math.floor(Math.random() * GLYPHS.length)]
}

function randomCode(length: number) {
  return Array.from({ length }, randomGlyph).join('')
}

